"use client";

import { Camera, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useRef, useState } from "react";
import CheckIcon from "@/assets/icons/check.svg";
import RetakeIcon from "@/assets/icons/retake.svg";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/core/analytics";
import { queryClient } from "@/core/caching";
import { getCameraInfo, getDeviceInfo } from "@/core/device";
import { useTranslation } from "@/core/i18n";
import { logger } from "@/core/logging";
import { assessmentSessionStorageService } from "@/features/assessments/AssessmentSessionStorageService";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import type { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { getRetrieveCurrentAssessmentQueryKey, setAssessmentPhotoDeviceInformation } from "@orval/ageless-assessments/ageless-assessments";
import { setAssessmentPhotoCaptureComplete } from "@orval/ageless-imaging/ageless-imaging";
import type { FrontCaptureResult, ProfileCaptureResult } from "../types";
import { PhotoPendingState } from "./photoConfirmationShared";
type ConfirmStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onConfirmed: () => void;
  onRetakeFront: () => void;
  onRetakeProfile: () => void;
  onRetakeAll: () => void;
  frontResult: FrontCaptureResult;
  profileResult: ProfileCaptureResult;
};
export function ConfirmStage({
  assessment,
  headers,
  onConfirmed,
  onRetakeFront,
  onRetakeProfile,
  onRetakeAll,
  frontResult,
  profileResult
}: ConfirmStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const isConfirmingRef = useRef(false);
  const canConfirm = !isConfirming;
  const frontPhotoSrc = frontResult.photoData || assessment.frontFaceImageUrl || null;
  const profilePhotoSrc = profileResult.profilePhotoData || assessment.profileFaceImageUrl || null;
  const trackRetake = useCallback((photoType: "front" | "profile" | "both") => {
    analytics.trackEvent("assessment_photo_retake_requested", {
      assessmentId: assessment.id,
      flowType,
      photoType,
      source: "capture_confirm"
    });
  }, [analytics, assessment.id, flowType]);
  const handleConfirm = useCallback(async () => {
    if (isConfirmingRef.current) return;
    if (!canConfirm) return;
    isConfirmingRef.current = true;
    setIsConfirming(true);
    setConfirmError(null);
    try {
      await setAssessmentPhotoCaptureComplete(assessment.id, {
        frontalFacialImageId: frontResult.uploadedImageId,
        selectedProfileFacialImageId: profileResult.profileImageId,
        selectedProfileSide: profileResult.profileSide
      } as {
        frontalFacialImageId: string;
        selectedProfileFacialImageId: string;
        selectedProfileSide: "left" | "right";
      }, {
        headers
      });
      assessmentSessionStorageService.persistStudioCaptureSelection(assessment.id, {
        frontResult,
        profileResult
      });
      await queryClient.invalidateQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });
      try {
        const deviceInfo = getDeviceInfo();
        const cameraInfo = await getCameraInfo();
        await setAssessmentPhotoDeviceInformation(assessment.id, {
          deviceInfo,
          cameraInfo: {
            hasCamera: cameraInfo.hasCamera,
            cameras: cameraInfo.cameras?.map(camera => camera.label || camera.deviceId).filter(Boolean) ?? null,
            facingModes: null,
            maxResolution: null
          }
        }, {
          headers
        });
      } catch (error_0) {
        logger.error("image_capture_device_info_failed", undefined, error_0 as Error);
      }
      analytics.trackEvent("assessment_profile_photos_confirmed", {
        flowType
      });
      onConfirmed();
    } catch (error) {
      logger.error("image_capture_confirm_failed", undefined, error as Error);
      setConfirmError(t("assessmentSteps.studioSession.profiles.uploadFailed"));
    } finally {
      setIsConfirming(false);
      isConfirmingRef.current = false;
    }
  }, [assessment, frontResult, profileResult, headers, canConfirm, analytics, flowType, onConfirmed, t]);
  return <motion.div animate={{
    opacity: 1
  }} className="inset-0 z-50 flex min-h-dvh flex-col overflow-y-auto bg-black/90" exit={{
    opacity: 0
  }} initial={{
    opacity: 0
  }}>
      <div className="pt-safe px-6 pt-6 text-center">
        <Text component="h2" text={t("assessmentSteps.studioSession.profiles.reviewTitle")} tone="white" variant="heading-xl" />
        <div className="mt-1">
          <Text component="p" text={t("assessmentSteps.studioSession.profiles.reviewSubtitleRetake")} tone="white" variant="body-sm" />
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-start px-6 py-6 sm:justify-center">
        <div className="w-full max-w-3xl">
          <div className="grid grid-cols-2 gap-3 sm:gap-5">
            <button className="relative aspect-3/4 w-full overflow-hidden rounded-2xl border border-white/20" onClick={() => {
            trackRetake("front");
            onRetakeFront();
          }}>
              {frontPhotoSrc ? <AppImage fill alt={t("assessmentSteps.studioSession.profiles.frontLabel")} className="object-cover" sizes="50vw" src={frontPhotoSrc} /> : <PhotoPendingState />}
              <div className="absolute top-3 right-3 rounded-full bg-black/70 p-2">
                <CheckIcon className="h-3.5 w-3.5 text-white **:stroke-current" />
              </div>
              <div className="type-caption absolute bottom-3 left-3 rounded-full bg-black/70 px-3 py-1 text-white">
                {t("assessmentSteps.studioSession.profiles.frontLabel")}
              </div>
              <div className="absolute right-3 bottom-3 rounded-full bg-black/70 p-2">
                <RetakeIcon className="h-4 w-4 text-white **:stroke-current" />
              </div>
            </button>

            {/* Profile photo */}
            <button className="relative aspect-3/4 w-full overflow-hidden rounded-2xl border border-white/20" onClick={() => {
            trackRetake("profile");
            onRetakeProfile();
          }}>
              {profilePhotoSrc ? <AppImage fill alt={t("assessmentSteps.studioSession.profiles.profileLabel")} className="object-cover" sizes="50vw" src={profilePhotoSrc} /> : <PhotoPendingState />}
              <div className="absolute top-3 right-3 rounded-full bg-black/70 p-2">
                <CheckIcon className="h-3.5 w-3.5 text-white **:stroke-current" />
              </div>
              <div className="type-caption absolute bottom-3 left-3 rounded-full bg-black/70 px-3 py-1 text-white">
                {t("assessmentSteps.studioSession.profiles.profileLabel")}
              </div>
              <div className="absolute right-3 bottom-3 rounded-full bg-black/70 p-2">
                <RetakeIcon className="h-4 w-4 text-white **:stroke-current" />
              </div>
            </button>
          </div>
        </div>

        {confirmError && <div className="border-destructive/40 bg-destructive/10 mx-auto mt-6 w-full max-w-lg rounded-xl border p-4">
            <Text component="p" text={confirmError} tone="white" variant="caption" />
          </div>}
      </div>

      <div className="pb-safe mx-auto w-full max-w-md px-6 pb-6">
        <div className="space-y-3">
          <Button className="text-foreground h-14 w-full bg-white hover:bg-white/90" disabled={isConfirming || !canConfirm} onClick={handleConfirm}>
            {isConfirming ? <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                <Text component="span" text={t("assessmentSteps.studioSession.profiles.reviewConfirming")} />
              </> : <>
                <CheckIcon className="mr-2 h-4 w-4" />
                <Text component="span" text={t("assessmentSteps.studioSession.preview.continue")} />
              </>}
          </Button>
          <button className="type-caption mx-auto flex items-center justify-center gap-1.5 pt-1 text-white/70 transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50" disabled={isConfirming} type="button" onClick={() => {
          trackRetake("both");
          onRetakeAll();
        }}>
            <Camera className="h-3.5 w-3.5 stroke-[1.5]" data-testid="confirm-retake-camera-icon" />
            <Text component="span" text={t("assessmentSteps.studioSession.profiles.retakeAll")} variant="caption" />
          </button>
        </div>
      </div>
    </motion.div>;
}